/**
 * @module Pipeline
 * @description Pipeline page — lists the Apex pipelines with LIVE health status.
 *
 * Health is fetched from GET /pipelines/health, which returns cached status
 * refreshed on a schedule from the Amazon Pipelines API (the browser cannot call
 * the Pipelines API directly — it has no browser/CORS support — so a backend
 * Lambda calls it and caches the result). The page polls every 60s and shows a
 * freshness stamp. Each row's health is one of: healthy | warning | failing |
 * unknown (unknown = the backend couldn't read that pipeline, e.g. not yet
 * refreshed or the refresh role lacks entitlement).
 *
 * Modular pipelines get a stage-level health table (Alpha/Beta/Gamma/Production,
 * grouped by team); Deployment pipelines keep the simple per-pipeline table.
 *
 * Pipeline membership, owners, and grouping all come from the API response so
 * the backend stays the single source of truth.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GitBranch, Rocket, ExternalLink, CheckCircle, AlertTriangle, XCircle, HelpCircle, RefreshCw } from 'lucide-react';
import { getPipelineHealth } from '../services/api';
import PipelineStageHealth from './PipelineStageHealth';

const PIPELINES_BASE_URL = 'https://pipelines.amazon.com/pipelines';
// Backend refreshes every 15 min; poll every 2 min — fresh enough, far fewer requests.
const POLL_INTERVAL_MS = 120000;

// Health status metadata — drives the status badge. `unknown` covers pipelines
// the backend could not read (not yet refreshed, or missing entitlement).
const HEALTH = {
  healthy: { label: 'Healthy', className: 'pipeline-health-healthy', Icon: CheckCircle },
  warning: { label: 'Warning', className: 'pipeline-health-warning', Icon: AlertTriangle },
  failing: { label: 'Failing', className: 'pipeline-health-failing', Icon: XCircle },
  unknown: { label: 'Unknown', className: 'pipeline-health-unknown', Icon: HelpCircle },
};

// A pipeline "needs attention" (red name) when it is failing.
function needsAttention(p) {
  return p.health === 'failing';
}

function HealthBadge({ health, reason }) {
  const meta = HEALTH[health] || HEALTH.unknown;
  const { label, className, Icon } = meta;
  return (
    <span className={`pipeline-health-badge ${className}`} title={reason || ''}>
      <Icon size={13} className="pipeline-health-icon" />
      {label}
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return 'never';
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function PipelineTable({ rows, showIndex = false }) {
  return (
    <table className="pipeline-table">
      <thead>
        <tr>
          {showIndex && <th className="pipeline-col-idx">#</th>}
          <th>Pipeline</th>
          <th>Owner</th>
          <th className="pipeline-col-health">Health</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={p.name}>
            {showIndex && <td className="pipeline-col-idx">{i + 1}</td>}
            <td>
              <a
                href={`${PIPELINES_BASE_URL}/${p.name}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`pipeline-link ${needsAttention(p) ? 'pipeline-link-attention' : ''}`}
              >
                {p.name}
                <ExternalLink size={12} className="pipeline-link-icon" />
              </a>
            </td>
            <td>{p.owner || <span className="pipeline-owner-empty">—</span>}</td>
            <td className="pipeline-col-health"><HealthBadge health={p.health} reason={p.reason} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Pipeline() {
  const [pipelines, setPipelines] = useState([]);
  const [oldestUpdated, setOldestUpdated] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const failuresRef = useRef(0);
  const inFlightRef = useRef(false);
  // Holds the pending poll timer and a scheduler that reschedules it. Kept in a
  // ref so fetches triggered outside the polling loop (manual refresh, tab
  // focus) can cancel a stale (backed-off) timer and restore the base cadence.
  const timerRef = useRef(null);
  const scheduleRef = useRef(() => {});

  const fetchHealth = useCallback(async () => {
    // In-flight guard: a scheduled tick, a visibilitychange, and the manual
    // Refresh button can all call this. `refreshing` is async React state the
    // timer/visibility paths never read, so it can't prevent overlap — an
    // out-of-order response could clobber `pipelines`/`error`. A ref is sync.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setRefreshing(true);
    try {
      const data = await getPipelineHealth();
      setPipelines(data.pipelines || []);
      setOldestUpdated(data.oldestUpdated || null);
      setThresholds(data.thresholds || null);
      setError('');
      failuresRef.current = 0;
    } catch (e) {
      setError(e.message || 'Failed to load pipeline health');
      failuresRef.current += 1;
    } finally {
      inFlightRef.current = false;
      setLoading(false);
      setRefreshing(false);
      // Reschedule from now so a fetch triggered outside the loop (manual /
      // visibility) cancels any pending timer and re-applies the current
      // cadence. On success failuresRef is 0, so a recovered endpoint drops
      // straight back to the base interval instead of waiting out a stale
      // long backoff timer.
      scheduleRef.current();
    }
  }, []);

  // Polling with three refinements over a bare setInterval:
  //   1. Cadence aligned to the backend (backend refreshes every 15 min, so a
  //      2-min poll is plenty fresh while cutting request volume vs. 60s).
  //   2. Visibility gating — a hidden/backgrounded tab does not poll, and
  //      returning to the tab triggers an immediate refresh.
  //   3. Exponential backoff on repeated errors, capped, so a failing endpoint
  //      is not hammered every interval indefinitely.
  useEffect(() => {
    const tick = async () => {
      if (document.visibilityState === 'visible') {
        await fetchHealth(); // reschedules itself in its finally block
      } else {
        scheduleRef.current();
      }
    };

    const scheduleNext = () => {
      const backoff = Math.min(failuresRef.current, 4); // cap the exponent
      const delay = POLL_INTERVAL_MS * Math.pow(2, backoff);
      clearTimeout(timerRef.current); // never leave a stale timer pending
      timerRef.current = setTimeout(tick, delay);
    };
    scheduleRef.current = scheduleNext;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchHealth();
    };

    fetchHealth(); // reschedules itself in its finally block
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchHealth]);

  // Split by category; Modular is a flat list, Deployment is grouped.
  const modular = pipelines.filter((p) => p.category === 'Modular');
  const deployment = pipelines.filter((p) => p.category === 'Deployment');
  const deploymentGroups = deployment.reduce((acc, p) => {
    const key = p.group || 'Other';
    (acc[key] = acc[key] || []).push(p);
    return acc;
  }, {});

  // Header health rollup.
  const healthCounts = pipelines.reduce(
    (acc, p) => {
      const key = HEALTH[p.health] ? p.health : 'unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    },
    { healthy: 0, warning: 0, failing: 0, unknown: 0 }
  );

  return (
    <div className="pipeline-page">
      <div className="pipeline-header">
        <div>
          <h2>Pipeline</h2>
          <p className="pipeline-subtitle">
            Apex modular and deployment pipelines with live health from the Pipelines API.
          </p>
        </div>
        <div className="pipeline-header-right">
          <div className="pipeline-health-summary">
            <span className="pipeline-health-badge pipeline-health-healthy">
              <CheckCircle size={13} className="pipeline-health-icon" />
              {healthCounts.healthy} Healthy
            </span>
            <span className="pipeline-health-badge pipeline-health-warning">
              <AlertTriangle size={13} className="pipeline-health-icon" />
              {healthCounts.warning} Warning
            </span>
            <span className="pipeline-health-badge pipeline-health-failing">
              <XCircle size={13} className="pipeline-health-icon" />
              {healthCounts.failing} Failing
            </span>
            {healthCounts.unknown > 0 && (
              <span className="pipeline-health-badge pipeline-health-unknown">
                <HelpCircle size={13} className="pipeline-health-icon" />
                {healthCounts.unknown} Unknown
              </span>
            )}
          </div>
          <div className="pipeline-freshness">
            <button
              className="btn btn-sm btn-secondary"
              onClick={fetchHealth}
              disabled={refreshing}
              title="Refresh now"
            >
              <RefreshCw size={14} className={refreshing ? 'pipeline-spin' : ''} />
            </button>
            <span className="pipeline-updated">Updated {relativeTime(oldestUpdated)}</span>
          </div>
        </div>
      </div>

      {error && <div className="pipeline-error">{error} <button onClick={() => setError('')}>×</button></div>}

      {loading ? (
        <div className="loading">Loading pipeline health…</div>
      ) : (
        <div className="pipeline-content">
          <section className="pipeline-section">
            <h3 className="pipeline-section-title">
              <GitBranch size={18} /> Modular
              <span className="pipeline-section-count">{modular.length}</span>
            </h3>
            {/* Stage-level health (Alpha/Beta/Gamma/Production) applies to Modular only. */}
            <PipelineStageHealth rows={modular} thresholds={thresholds} />
          </section>

          <section className="pipeline-section">
            <h3 className="pipeline-section-title">
              <Rocket size={18} /> Deployment
              <span className="pipeline-section-count">{deployment.length}</span>
            </h3>
            {Object.entries(deploymentGroups).map(([group, rows]) => (
              <div key={group} className="pipeline-subgroup">
                <h4 className="pipeline-subgroup-title">{group}</h4>
                <PipelineTable rows={rows} />
              </div>
            ))}
          </section>
        </div>
      )}
    </div>
  );
}

export default Pipeline;
